import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Calendar, Megaphone, Trophy } from 'lucide-react';
import { motion } from 'framer-motion';

const services = [
  {
    id: 'stall-building',
    icon: Building2,
    title: 'Trade Fair Stall Building',
    description: 'Custom-designed exhibition booths that captivate audiences and elevate your brand presence at trade fairs worldwide.',
    image: 'https://images.unsplash.com/photo-1761195689615-9469b65dac01?crop=entropy&cs=srgb&fm=jpg&q=85',
    features: ['Custom Designs', '3D Visualization', 'Turnkey Solutions'],
    link: '/services/stall-building'
  },
  {
    id: 'corporate-events',
    icon: Calendar,
    title: 'Corporate Events',
    description: 'End-to-end corporate event management from conferences to product launches, delivering memorable business experiences.',
    image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?crop=entropy&cs=srgb&fm=jpg&q=85',
    features: ['Conferences', 'Product Launches', 'Award Ceremonies'],
    link: '/services/corporate-events'
  },
  {
    id: 'live-events',
    icon: Trophy,
    title: 'Live Events',
    description: 'Spectacular live experiences from marathons to corporate galas, creating moments that inspire and unite audiences.',
    image: 'https://images.unsplash.com/photo-1746046489457-9628dc3b8a1f?crop=entropy&cs=srgb&fm=jpg&q=85',
    features: ['Event Production', 'Stage Management', 'Live Broadcasting'],
    link: '/services/live-events'
  },
  {
    id: 'brand-activation',
    icon: Megaphone,
    title: 'Brand Activation',
    description: 'Strategic brand experiences that engage consumers, build loyalty, and drive measurable business results.',
    image: 'https://images.pexels.com/photos/29708240/pexels-photo-29708240.jpeg',
    features: ['Consumer Engagement', 'Experiential Marketing', 'ROI Tracking'],
    link: '/services/brand-activation'
  }
];

export const ServicesSection = () => {
  return (
    <section className="py-20 lg:py-32 bg-white relative overflow-hidden" data-testid="services-section">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 right-0 w-96 h-96 bg-navy rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gold rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block text-gold font-semibold uppercase tracking-wider text-sm mb-4"
          >
            What We Offer
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="font-heading text-3xl md:text-4xl lg:text-5xl text-navy mb-6"
          >
            Comprehensive Event Solutions
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-muted-foreground text-lg"
          >
            From concept to execution, we deliver world-class event experiences that elevate brands and create lasting impressions.
          </motion.p>
        </div>

        {/* Services Grid - Bento Layout */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              data-testid={`service-card-${service.id}`}
            >
              <Link 
                to={service.link}
                className="service-card block bg-white border border-gray-100 p-8 hover:shadow-2xl transition-all duration-500 group h-full"
              >
                <div className="flex flex-col lg:flex-row gap-6 h-full">
                  {/* Image */}
                  <div className="lg:w-1/2 h-48 lg:h-auto overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>

                  {/* Content */}
                  <div className="lg:w-1/2 flex flex-col justify-center">
                    <div className="w-14 h-14 bg-navy/5 flex items-center justify-center mb-5 group-hover:bg-gold/20 transition-colors">
                      <service.icon className="w-7 h-7 text-navy group-hover:text-gold transition-colors" />
                    </div>
                    
                    <h3 className="font-heading text-xl lg:text-2xl text-navy mb-3 group-hover:text-gold transition-colors">
                      {service.title}
                    </h3>
                    
                    <p className="text-muted-foreground mb-5 line-clamp-3">
                      {service.description}
                    </p>

                    {/* Features */}
                    <div className="flex flex-wrap gap-2 mb-5">
                      {service.features.map((feature) => (
                        <span 
                          key={feature}
                          className="text-xs bg-muted text-muted-foreground px-3 py-1"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>

                    <div className="inline-flex items-center gap-2 text-navy font-semibold text-sm uppercase tracking-wider group-hover:text-gold transition-colors">
                      Learn More
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* View All Services */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <Link 
            to="/services" 
            className="btn-secondary inline-flex items-center gap-2"
            data-testid="view-all-services-button"
          >
            View All Services
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
